import { c as _c } from "react/compiler-runtime";
import React, { FunctionComponent, ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { FormGroup } from 'record-it-components/form/FormGroup/FormGroup';
import { Checkbox } from 'record-it-components/input/Checkbox/Checkbox';
import { LocationInput } from 'record-it-components/input/LocationInput/LocationInput';
import { BufferedTextInput } from 'record-it-components/input/TextInput/TextInput';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { ConstructionSite } from '../../../classes/entities/ConstructionSite/constructionSiteTypes';
import { AnyPropertyChanged, useEntityPropertyChangedCallback } from '../../../hooks/entity/useEntityPropertyChangedCallback';
import { TimeTrackingActivityCompilationSelect } from '../../timeTrackingActivityCompilation/TimeTrackingActivityCompilationSelect/TimeTrackingActivityCompilationSelect';
export type EditConstructionSiteFormProps = PropsWithoutChildren<ComponentProps<'div'>> & {
  constructionSite: ConstructionSite;
  onPropertyChanged: AnyPropertyChanged<ConstructionSite>;
};
export const EditConstructionSiteForm: FunctionComponent<EditConstructionSiteFormProps> = React.memo(props => {
  const $ = _c(37);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props,
      ignoreChildren: true
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let constructionSite;
  let onPropertyChanged;
  let remainingProps;
  if ($[2] !== t0) {
    ({
      constructionSite,
      onPropertyChanged,
      ...remainingProps
    } = t0);
    $[2] = t0;
    $[3] = constructionSite;
    $[4] = onPropertyChanged;
    $[5] = remainingProps;
  } else {
    constructionSite = $[3];
    onPropertyChanged = $[4];
    remainingProps = $[5];
  }
  const {
    t
  } = useTranslation();
  const handleNameChanged = useEntityPropertyChangedCallback(constructionSite, "name", onPropertyChanged);
  const handleArchivedChanged = useEntityPropertyChangedCallback(constructionSite, "archived", onPropertyChanged);
  const handleCompilationIdChanged = useEntityPropertyChangedCallback(constructionSite, "compilationId", onPropertyChanged);
  const handleLocationChanged = useEntityPropertyChangedCallback(constructionSite, "location", onPropertyChanged);
  let t1;
  if ($[6] !== t) {
    t1 = t("entities.ConstructionSite.name");
    $[6] = t;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== constructionSite.name || $[9] !== handleNameChanged || $[10] !== t1) {
    t2 = <FormGroup><BufferedTextInput label={t1} value={constructionSite.name} onInput={handleNameChanged} data-testid="EditConstructionSiteForm.NameInput" /></FormGroup>;
    $[8] = constructionSite.name;
    $[9] = handleNameChanged;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== t) {
    t3 = t("entities.ConstructionSite.archived");
    $[12] = t;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== constructionSite.archived || $[15] !== handleArchivedChanged || $[16] !== t3) {
    t4 = <FormGroup><Checkbox label={t3} value={constructionSite.archived} onValueChanged={handleArchivedChanged} /></FormGroup>;
    $[14] = constructionSite.archived;
    $[15] = handleArchivedChanged;
    $[16] = t3;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const t5 = constructionSite.compilationId;
  let t6;
  if ($[18] !== t) {
    t6 = t("entities.ConstructionSite.compilationId");
    $[18] = t;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== constructionSite.compilationId || $[21] !== constructionSite.ownerUserGroupId || $[22] !== handleCompilationIdChanged || $[23] !== t6) {
    t7 = <FormGroup><TimeTrackingActivityCompilationSelect value={t5} label={t6} userGroupId={constructionSite.ownerUserGroupId} onValueChanged={handleCompilationIdChanged} data-testid="EditConstructionSiteForm.CompilationSelect" /></FormGroup>;
    $[20] = constructionSite.compilationId;
    $[21] = constructionSite.ownerUserGroupId;
    $[22] = handleCompilationIdChanged;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  const t8 = constructionSite.location;
  let t9;
  if ($[25] !== t) {
    t9 = t("entities.ConstructionSite.location");
    $[25] = t;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== constructionSite.location || $[28] !== handleLocationChanged || $[29] !== t9) {
    t10 = <FormGroup><LocationInput value={t8} label={t9} onValueChanged={handleLocationChanged} /></FormGroup>;
    $[27] = constructionSite.location;
    $[28] = handleLocationChanged;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== remainingProps || $[32] !== t10 || $[33] !== t2 || $[34] !== t4 || $[35] !== t7) {
    t11 = <div {...remainingProps}>{t2}{t4}{t7}{t10}</div>;
    $[31] = remainingProps;
    $[32] = t10;
    $[33] = t2;
    $[34] = t4;
    $[35] = t7;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  return t11;
});